/**
 * One guarded reload when a code chunk fails to load.
 *
 * A tab running an older release asks for content-hashed chunks by the names its own build gave
 * them. A release keeps old chunks, but if one is gone (or the network dropped it) the screen cannot
 * load; reloading fetches the current shell (served `no-cache`) and its current chunks. The reload
 * happens at most once per {@link RETRY_WINDOW_MS} per tab, so a chunk that is broken even after the
 * reload shows the normal error screen instead of reloading forever. The same idea (and key) as the
 * customer portal's lazy-route boundary, here as one helper for the staff app's error surfaces.
 */

export const CHUNK_RELOAD_KEY = 'pipe-chunk-reload';
export const RETRY_WINDOW_MS = 5 * 60_000;

const CHUNK_MESSAGE =
  /Loading (CSS )?chunk [^\s]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

export function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  return (
    name === 'ChunkLoadError' ||
    (typeof message === 'string' && CHUNK_MESSAGE.test(message))
  );
}

export interface ChunkReloadDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload?: () => void;
  now?: number;
}

function sessionStore(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Reloads the page when `error` is a chunk-load failure and no such reload ran recently.
 *  Returns whether a reload was started. */
export function reloadForChunkError(
  error: unknown,
  deps: ChunkReloadDeps = {}
): boolean {
  if (!isChunkLoadError(error)) return false;
  const storage = deps.storage === undefined ? sessionStore() : deps.storage;
  const now = deps.now ?? Date.now();
  try {
    if (!storage) return false; // without a guard a reload could loop: show the error instead
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < RETRY_WINDOW_MS)
      return false;
    storage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  (deps.reload ?? (() => window.location.reload()))();
  return true;
}
